import { formatInvoiceAmount as formatCurrencyAmount } from '../../services/userInvoiceService';
import { getFullName } from '../../utils/formatters';
import { UserPlan } from '../../types/api';
import { InvoiceDetails, InvoiceStatus } from './types';

const toAmount = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatIssueDate = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getPlanReference = (plan: UserPlan): string => {
  const ref = plan.unique_id || plan.planId || plan.user_id;
  return ref.startsWith('#') ? ref : `#INV-${ref}`;
};

const mapPlanStatus = (plan: UserPlan): InvoiceStatus => {
  if (plan.is_active) return 'Paid';
  return 'Pending';
};

export const mapUserPlanToInvoiceDetails = (plan: UserPlan): InvoiceDetails => {
  const basePrice = toAmount(plan.base_price);
  const taxPrice = toAmount(plan.tax_price);
  const totalAmount = toAmount(plan.total_price) || basePrice + taxPrice;
  const taxRate =
    basePrice > 0 ? Math.round((taxPrice / basePrice) * 1000) / 10 : 0;

  return {
    invoiceId: getPlanReference(plan),
    clientName: getFullName(plan.first_name, null, plan.last_name),
    issueDate: formatIssueDate(plan.start_date),
    status: mapPlanStatus(plan),
    lineItems: [
      {
        description: `${plan.name} - ${plan.duration_months} month plan`,
        amount: basePrice,
      },
      {
        description:
          taxRate > 0 ? `Applicable Taxes (${taxRate}%)` : 'Applicable Taxes',
        amount: taxPrice,
        muted: true,
      },
    ],
    totalAmount,
    currency: plan.currency ?? 'INR',
    noteFromAdmin: plan.note_from_admin ?? null,
  };
};

export interface InvoiceHistoryRow {
  invoiceId: string;
  status: string;
  date: string;
  planName: string;
  amount: string;
  noteFromAdmin?: string | null;
}

export const mapInvoiceHistoryToDetails = (
  row: InvoiceHistoryRow,
  clientName: string
): InvoiceDetails => {
  const numericAmount = parseFloat(row.amount.replace(/[^\d.]/g, '')) || 0;
  const taxAmount = Math.round(numericAmount * 0.075 * 100) / 100;
  const baseAmount = Math.max(numericAmount - taxAmount, 0);

  return {
    invoiceId: row.invoiceId,
    clientName,
    issueDate: row.date,
    status: (row.status as InvoiceStatus) || 'Paid',
    lineItems: [
      { description: row.planName, amount: baseAmount },
      {
        description: 'Applicable Taxes (7.5%)',
        amount: taxAmount,
        muted: true,
      },
    ],
    totalAmount: numericAmount,
    noteFromAdmin: row.noteFromAdmin ?? null,
  };
};

export const formatInvoiceAmount = (amount: number, currency = 'INR'): string =>
  formatCurrencyAmount(amount, currency);
